import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { api } from "../../services/api";
import GitStatusCell from "./GitStatus";

const HEADERS = [
  { key: "applied", label: " " },
   { key: "snow", label: "Gsnow No#" },
  { key: "transition", label: "Instance Type" },
  { key: "prevPrice", label: "Previous Price", align: "right" },
  { key: "newPrice", label: "New Price", align: "right" },
  { key: "resizeStatus", label: "Resize Status" },
  { key: "gitStatus", label: "Git Status" },
  { key: "scheduledOn", label: "Scheduled On" },
  { key: "requestedBy", label: "Requested By" },
  { key: "requestedOn", label: "Requested On" },

  {key:"updatedOn", label:"Updated On"},
];

const STATUS_STYLES = {
  completed: { bg: "#dcfce7", fg: "#166534", dot: "#16a34a" },
  success: { bg: "#dcfce7", fg: "#166534", dot: "#16a34a" },
  failed: { bg: "#fee2e2", fg: "#991b1b", dot: "#dc2626" },
  rejected: { bg: "#fee2e2", fg: "#991b1b", dot: "#dc2626" },
  inprogress: { bg: "#fef3c7", fg: "#92400e", dot: "#f59e0b" },
  "in progress": { bg: "#fef3c7", fg: "#92400e", dot: "#f59e0b" },
  pending: { bg: "#e0e7ff", fg: "#3730a3", dot: "#6366f1" },
};

const money = (v) =>
  v === null || v === undefined || v === "" || Number.isNaN(Number(v))
    ? "—"
    : `$${parseFloat(v).toFixed(2)}`;

const StatusPill = ({ value }) => {
  const key = String(value || "").toLowerCase().trim();
  const s = STATUS_STYLES[key] || { bg: "#f1f5f9", fg: "#475569", dot: "#94a3b8" };
  return (
    <Chip
      size="small"
      label={value || "—"}
      sx={{
        bgcolor: s.bg,
        color: s.fg,
        fontWeight: 600,
        fontSize: "0.72rem",
        borderRadius: "6px",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

const TypeTransition = ({ from, to }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Chip
      size="small"
      label={from || "—"}
      sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600, fontSize: "0.72rem", borderRadius: "6px" }}
    />
    <ArrowRightAltIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
    <Chip
      size="small"
      label={to || "—"}
      sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 700, fontSize: "0.72rem", borderRadius: "6px" }}
    />
  </Box>
);

const cardStyle = { borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "none" };

const VMResizeHistoryTable = ({
  resourceId,
  resourceOverview,
  rows: externalRows,
  endpoint,
  onLoaded,
  refreshKey,
  dense = false,
}) => {
  const [rows, setRows] = useState(externalRows || []);
  const [loading, setLoading] = useState(false);
  const controlled = Array.isArray(externalRows);

  const targetId = resourceId || resourceOverview?.resource_Id;

  const fetchHistory = useCallback(async () => {
    if (controlled || !targetId) return;
    setLoading(true);
    try {
      const url = endpoint || `/api/Resources/GetVMResizeHistory/${targetId}`;
      const response = await api.get(url);
      const data = response.data || [];
      setRows(data);
      onLoaded?.(data);
    } catch (error) {
      console.error("Failed to load resize history", error);
      setRows([]);
      onLoaded?.([]);
    } finally {
      setLoading(false);
    }
  }, [controlled, targetId, endpoint, onLoaded]);

  useEffect(() => {
    if (controlled) {
      setRows(externalRows);
      return;
    }
    fetchHistory();
  }, [fetchHistory, refreshKey, controlled, externalRows]);

  const showEmpty = !loading && rows.length === 0;


    const formatDate = (dateString) => {
    if (!dateString) return ''; // Return an empty string if dateString is undefined


    // Remove the milliseconds part if it exists
    const correctedDateString = dateString.split('.')[0] + 'Z'; // Add 'Z' to denote UTC


    const date = new Date(correctedDateString);

    if (Number.isNaN(date.getTime())) {
      console.error('Invalid date:', correctedDateString); // Log if the date is invalid
      return 'Invalid Date';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <Card sx={{ mt: 2, border: 'none', background: 'none', boxShadow: 'none', }}>
      <CardContent sx={{p:0}}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#0f172a" }}>
              Resize History
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
  

  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        bgcolor: "#16a34a",
      }}
    />
    <Typography
      sx={{
        fontSize: "0.75rem",
        color: "#475569",
      }}
    >
      Applied to Resource
    </Typography>
  </Box>

  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        bgcolor: "#dc2626",
      }}
    />
    <Typography
      sx={{
        fontSize: "0.75rem",
        color: "#475569",
      }}
    >
      Not Applied to Resource
    </Typography>
  </Box>

  {!controlled && (
    <Tooltip title="Refresh history">
      <IconButton
        size="small"
        onClick={fetchHistory}
        disabled={loading}
      >
        {loading ? (
          <CircularProgress size={16} />
        ) : (
          <RefreshIcon sx={{ fontSize: 18 }} />
        )}
      </IconButton>
    </Tooltip>
  )}
</Box>
</Box>

        {/* Empty state */}
        {showEmpty ? (
          <Box
            sx={{
              py: 6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              border: "1px dashed #e2e8f0",
              borderRadius: "10px",
              bgcolor: "#f8fafc",
            }}
          >
            <HistoryToggleOffIcon sx={{ fontSize: 32, color: "#94a3b8" }} />
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#475569" }}>
              No existing VM Resize history found
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
              Submitted resize requests will appear here with their approval and Git status.
            </Typography>
          </Box>
        ) : (
          <TableContainer
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              maxHeight: 420,
            }}
          >
            <Table stickyHeader size={dense ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  {HEADERS.map((h) => (
                    <TableCell
                      key={h.key}
                      align={h.align || "left"}
                      sx={{
                        bgcolor: "#f8fafc",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: "#64748b",
                        letterSpacing: "0.4px",
                        whiteSpace: "nowrap",
                        borderBottom: "1px solid #e2e8f0",
                        width: h.key === "spacer" ? 8 : undefined,
                      }}
                    >
                      {h.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading
                  ? Array.from({ length: 1 }).map((_, i) => (
                      <TableRow key={`sk-${i}`}>
                        {HEADERS.map((h) => (
                          <TableCell key={h.key}>
                            <Skeleton variant="text" width={h.key === "spacer" ? 4 : "80%"} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : rows.map((row, idx) => (
                      <TableRow
                        key={row.vmResizeRequestId || row.resizeId || idx}
                        hover
                        sx={{ "&:last-child td": { borderBottom: 0 } }}
                      >
<TableCell>
  <Tooltip
    title={
      row.isAppliedToResource
        ? "Applied to Resource"
        : "Not Applied to Resource"
    }
  >
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        bgcolor: row.isAppliedToResource
          ? "#16a34a"
          : "#dc2626",
        mx: "auto",
      }}
    />
  </Tooltip>
</TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color:"#2563eb"  }}>
                            {row.snowChangeNumber || "—"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <TypeTransition from={row.previousInstanceType} to={row.newInstanceType} />
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: "0.8rem", color: "#475569" }}>
                            {money(row.previousPrice)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#0f172a" }}>
                            {money(row.newPrice)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusPill value={row.resizeStatus} />
                        </TableCell>

                        <TableCell>
                          <GitStatusCell row={row} />
                        </TableCell>
   <TableCell>
                          <Typography sx={{ fontSize: "0.8rem", color: "#475569" }}>
                            {formatDate(row.scheduledOn)|| "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: "0.8rem", color: "#475569" }}>
                            {row.createdByName || "-"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: "0.8rem", color: "#475569", whiteSpace: "nowrap" }}>
                            {/* {row.createdOn
                              ? new Date(row.createdOn).toISOString().split("T")[0]
                              : "—"} */}
                              {formatDate(row.createdOn)|| "-"}
                          </Typography>
                        </TableCell>
                         <TableCell>
                          <Typography sx={{ fontSize: "0.8rem", color: "#475569", whiteSpace: "nowrap" }}>
                            {/* {row.updatedOn
                              ? new Date(row.updatedOn).toISOString().split("T")[0]
                              : "—"} */}
                               {formatDate(row.updatedOn)|| "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default VMResizeHistoryTable;